import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiGithub, FiLinkedin, FiInstagram, FiMail } from 'react-icons/fi';

const FooterContainer = styled.footer`
  background: transparent;
  color: #fff;
  padding: 3rem 0 2rem;
  margin-top: 4rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    padding: 2rem 0 1.5rem;
    margin-top: 3rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 0 1rem;
    margin-top: 2rem;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 0 1rem;
  }
  
  @media (max-width: 480px) {
    gap: 1.5rem;
    padding: 0 0.8rem;
  }
`;

const FooterSection = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 255, 0, 0.2);
  border-radius: 16px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    background: linear-gradient(45deg, #ffffff, #00ff00);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  p {
    color: #cbd5e1;
    line-height: 1.6;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    
    h3 {
      font-size: 1.3rem;
    }
  }
  
  @media (max-width: 480px) {
    padding: 1.2rem;
    border-radius: 12px;
    
    h3 {
      font-size: 1.2rem;
    }
    
    p {
      font-size: 0.9rem;
      line-height: 1.7;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialLink = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: rgba(0, 255, 0, 0.1);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 50%;
  color: #00ff00;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 255, 0, 0.2);
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 255, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(0, 255, 0, 0.2);
  color: #94a3b8;
  
  span {
    color: #00ff00;
  }
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Anas Portfolio
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            A passionate Full Stack Developer creating innovative web solutions with modern technologies.
          </motion.p>
        </FooterSection>
        
        <FooterSection>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Connect With Me
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Let's work together on your next project.
          </motion.p>
          <SocialLinks>
            <SocialLink
              href="https://github.com/Anasnaveed69"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="cursor-target"
            >
              <FiGithub size={20} />
            </SocialLink>
            <SocialLink
              href="https://www.linkedin.com/in/anas-butt-8661992aa/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1, rotate: 5 }}
              whileTap={{ scale: 0.95 }}
              data-cursor-text="LinkedIn"
              className="cursor-target"
            >
              <FiLinkedin size={20} />
            </SocialLink>
            <SocialLink
              href="https://www.instagram.com/butt._.anas/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="cursor-target"
            >
              <FiInstagram size={20} />
            </SocialLink>
            <SocialLink
              href="mailto:buttanas813@gmail.com"
              whileHover={{ scale: 1.1, rotate: 5 }}
              whileTap={{ scale: 0.95 }}
              data-cursor-text="Email"
              className="cursor-target"
            >
              <FiMail size={20} />
            </SocialLink>
          </SocialLinks>
        </FooterSection>
      </FooterContent>
      
      <Copyright>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          © 2025 Anas Portfolio. Made with <span>❤️</span> 
        </motion.p>
      </Copyright>
    </FooterContainer>
  );
};

export default Footer;
